import planck from 'planck-js';
import * as PIXI from 'pixi.js';
import GSObject from '../../Base/GSObject';
import Global from '../../Global';
import Vector from '../../Base/Math/Vector';
import GSObjectArray from '../../Base/GSObjectArray';
import State, { AppearanceTypes } from './State';

import { Shape, ViewDirection } from '../../../GameKit/shared';
import ActieReactie from './ActieReactie';
import AssetManager from '../../Base/AssetManager';
import Angle from '../../Base/Math/Angle';

export { Shape, AppearanceTypes };

export default class Blok extends GSObject {
    constructor(gsoParent) {
        super(gsoParent);

        this.level = gsoParent;

        this.name = 'Blok';

        this.sprite = null;
        this.pixiContainer = null;
        this.pixiStateContainer = null;

        this.physicsBody = null;
        this.physicsShape = null;

        this.position = new Vector(200, 150);
        this.size = new Vector(1, 1);
        this.rotation = 0;

        this.shape = Shape.rectangle;

        this.states = new GSObjectArray(this, State);
        this.states.push(new State(this));
        this._activeState = this.states.setActive(0);

        this.actieReacties = new GSObjectArray(this, ActieReactie);
        this.actieReacties.push(new ActieReactie(this));
        this._activeActieReactie = this.actieReacties.setActive(0);

        this.culled = false;

        // Runtime
        this.scale = new Vector(1, 1);
        this.nextScale = new Vector(1, 1);
        this._setScale = new Vector(1, 1);
    }

    setCulled(culled) {
        if (this.culled !== culled) {
            this.culled = culled;
            if (this._hasVisuals && this.culled) {
                this.pixiContainer.visible = false;
            } else if (this._hasVisuals && !this.culled) {
                this.pixiContainer.visible = true;
                this.pixiContainer.updateTransform();
            }
        }
    }

    getSurfaceArea() {
        if (this.shape === Shape.rectangle) {
            return this.size.x * this.size.y;
        }
        return Math.PI * Math.pow(this.size.x, 2);
    }

    getWeight() {
        return this.getSurfaceArea() * (this.getActiveState().weight / 30);
    }

    getIndex() {
        const i = this._gsoParent.blokken.indexOf(this);
        if (i < 0) throw new Error("Blok is not part of the parent's list of blokken.");
        return i;
    }

    getActiveState() {
        if (!this._activeState) throw new Error('Blok has no active state set. A Blok should always have a state set.');
        return this._activeState;
    }

    getActiveActieReactie() {
        if (!this._activeActieReactie) throw new Error('Blok has no active actie reactie set.');
        return this._activeActieReactie;
    }

    setActiveState(index) {
        this._activeState = this.states.setActive(index, this._hasVisuals, this._hasBody);
        this.scale = new Vector(1, 1);
        this.nextScale = new Vector(1, 1);
        try {
            this.actieReacties.setActive(index, this._hasVisuals, this._hasBody);
        } catch {
            if (index === 0) {
                this.actieReacties[0].enable();
                if (this._hasBody) this.actieReacties[0]._gsoCreateBody();
            } else {
                this.actieReacties[0].disable();
            }
            // Not found, all ActiesReacties are disabled.
        }
    }

    createSimplifiedVisuals() {
        if (this._hasVisuals) {
            this._gsoDestroyVisuals();
            this._hasVisuals = true;
        }
        const game = this.getGame();

        let ySize = this.size.y;
        if (this.shape === Shape.circle) {
            ySize = this.size.x;
        }
        this.pixiContainer = new PIXI.Container();
        this.pixiContainer.x = this.position.x;
        this.pixiContainer.y = this.position.y;

        this.simplifiedSprite = new PIXI.Sprite(AssetManager.get('img.white'), 16, 16);
        this.simplifiedSprite.width = this.size.x * Global.NATIVE_BLOCK_SIZE;
        this.simplifiedSprite.height = ySize * Global.NATIVE_BLOCK_SIZE;
        this.simplifiedSprite.anchor.set(0.5);
        this.pixiContainer.addChild(this.simplifiedSprite);
        this._gsoParent.pixiContainerBlokLayer.addChild(this.pixiContainer);

        if (game.isCanvas()) {
            this.simplifiedSprite.alpha = 0.5;
        }

        if (this.shape === Shape.circle) {
            this.circleMaskGraphics = new PIXI.Graphics();
            this.circleMaskGraphics.beginFill(0x000000);
            this.circleMaskGraphics.drawCircle(0, 0, (Global.NATIVE_BLOCK_SIZE * this.size.x) / 2);
            this.circleMaskGraphics.endFill();
            this.simplifiedSprite.mask = this.circleMaskGraphics;
            this.pixiContainer.addChild(this.circleMaskGraphics);
        }
    }

    onCreateVisuals() {
        this.pixiContainer = new PIXI.Container();
        this.pixiContainer.x = this.getPosition().x;
        this.pixiContainer.y = this.getPosition().y;
        this.pixiContainer.rotation = this.rotation;
        this._gsoParent.pixiContainerBlokLayer.addChild(this.pixiContainer);

        this.pixiStateContainer = new PIXI.Container();
        this.pixiContainer.addChild(this.pixiStateContainer);
    }

    getPosition() {
        if (this._hasBody) {
            return new Vector(this.physicsBody.getPosition().x * Global.PHYSICS_SCALE, this.physicsBody.getPosition().y * Global.PHYSICS_SCALE);
        }
        return this.position.copy();
    }

    onUpdateVisuals() {
        if (this._hasBody) {
            // If we have a physics presence then use that info.
            this.pixiContainer.x = this.getGame().deltaLerp(this.pixiContainer.x, this.physicsBody.getPosition().x * Global.PHYSICS_SCALE);
            this.pixiContainer.y = this.getGame().deltaLerp(this.pixiContainer.y, this.physicsBody.getPosition().y * Global.PHYSICS_SCALE);

            this.pixiContainer.rotation = this.getGame().angleDeltaLerp(this.pixiContainer.rotation, this.physicsBody.getAngle());

            this.scale.x = this.getGame().deltaLerp(this.scale.x, this.nextScale.x);
            if (this.shape === Shape.rectangle) {
                this.scale.y = this.getGame().deltaLerp(this.scale.y, this.nextScale.y);
            } else this.scale.y = this.scale.x;
        } else {
            this.pixiContainer.x = this.position.x;
            this.pixiContainer.y = this.position.y;
            this.pixiContainer.rotation = this.rotation;
        }
        this.pixiContainer.scale.x = this.scale.x;
        this.pixiContainer.scale.y = this.scale.y;
    }

    onDestroyVisuals() {
        this.pixiContainer.destroy();
        this.pixiContainer = null;
        if (this.pixiStateContainer) {
            this.pixiStateContainer.destroy();
            this.pixiStateContainer = null;
        }
        if (this.circleMaskGraphics) {
            this.circleMaskGraphics.destroy();
            this.circleMaskGraphics = null;
        }
        if (this.simplifiedSprite) {
            this.simplifiedSprite.destroy();
            this.simplifiedSprite = null;
        }
    }

    onCreateBody(physicsWorld) {
        this.physicsBody = physicsWorld.createBody();
        this.physicsBody.parentBlok = this;
        this.physicsBody.setPosition(planck.Vec2(this.position.x / Global.PHYSICS_SCALE, this.position.y / Global.PHYSICS_SCALE));
        this.physicsBody.setAngle(this.rotation);
        this.physicsBody.m_isSensor = true;

        if (this.shape === Shape.rectangle) {
            this.physicsShape = planck.Box((this.size.x * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE / 2, (this.size.y * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE / 2);
        } else {
            this.physicsShape = planck.Circle((this.size.x * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE / 2);
        }
        if (this.level.viewDirection === ViewDirection.Side) {
            this.physicsBody.setLinearDamping(0.2);
            this.physicsBody.setAngularDamping(0.2);
        } else {
            this.physicsBody.setLinearDamping(2.5);
            this.physicsBody.setAngularDamping(2.5);
        }
    }

    onDestroyBody(physicsWorld) {
        this.scale = new Vector(1, 1);
        this._setScale = new Vector(1, 1);
        this.nextScale = new Vector(1, 1);
        physicsWorld.destroyBody(this.physicsBody);
        this._activeState = this.states.setActive(0, this._hasVisuals);
        this.actieReacties.setActive(0, this._hasVisuals);
        this.enable();
    }

    onFixedUpdate() {
        if (this._hasBody) {
            if (this._setScale.x !== this.scale.x || this._setScale.y !== this.scale.y) {
                // Scale has changed so update our physicsbody
                if (this.shape === Shape.rectangle) {
                    this.physicsShape = planck.Box(
                        ((this.size.x * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE / 2) * this.scale.x,
                        ((this.size.y * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE / 2) * this.scale.y
                    );
                } else {
                    this.physicsShape = planck.Circle(((this.size.x * Global.NATIVE_BLOCK_SIZE) / Global.PHYSICS_SCALE / 2) * this.scale.x);
                }
                this.physicsBody.destroyFixture(this._activeState.physicsFixture);
                this._activeState.physicsFixture = this.physicsBody.createFixture(this.physicsShape, this._activeState.shapeDef);
                this._setScale = this.scale.copy();
            }
        }
    }

    // Deserialize the given JSON object.
    deserialize(json) {
        this.autoDeserialize(json);
        this._activeState = this.states.setActive(0);
        this._activeActieReactie = this.actieReacties.setActive(0);
        this.requestNewVisuals();
    }
}

Blok.serializeableFields = {
    uuid: String,
    name: String,
    position: Vector,
    size: Vector,
    rotation: Angle,
    shape: Number,
    states: [State],
    actieReacties: [ActieReactie]
};
